/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-26 22:37:07
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, reset, getFormValues } from 'redux-form';
import { Modal, FormLayout, Stack } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';


import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import RenderAutocompleteV2 from 'src/components/views/common/autocomplete-hoc-v2';
import {
  buildInitialValues,
  buildProductVisibilityRule
} from 'src/helpers/product-visibility-helper';
import * as productVisibilityActions from 'src/stores/actions/product-visibility-actions';

type Props = {
  active: boolean,
  selectedId: number,
  handleChange: () => void,
  updateProductVisibilityRule: () => void
};

type ComposedProps = Props & WithI18nProps;

class ProductVisibilityModal extends Component<ComposedProps> {
  render() {
    const {
      i18n,
      active,
      saving,
      handleSubmit,
      pristine,
      selectedId,
      submiting,
      tags,
      collectionPage,
      searchPage,
      instantSearchWidget
    } = this.props;

    const title =
      selectedId === -1
        ? i18n.translate('AdminHTML.ProductVisibility.Edit.title_Add')
        : i18n.translate('AdminHTML.ProductVisibility.Edit.title_Edit');

    return (
      <Modal
        open={active}
        onClose={this.handleChange}
        title={title}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.save'),
          loading: saving,
          onAction: handleSubmit(this.handleSave.bind(this)),
          disabled:
            pristine ||
            submiting ||
            _.isEmpty(tags) ||
            !(collectionPage || searchPage || instantSearchWidget)
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: this.handleChange
          }
        ]}>
        <Modal.Section>
          <div className="BC-Modal__ProductVisibility">
            <form onSubmit={this.handleDefaultSubmit}>
              <FormLayout>
                <Field
                  name="tags"
                  component={RenderAutocompleteV2}
                  label={i18n.translate('AdminHTML.ProductVisibility.ProductVisibility.tags_Label')}
                  value={tags}
                  type={'tags'}
                  filterPrefix={''}
                  placeholder={i18n.translate(
                    'AdminHTML.ProductVisibility.ProductVisibility.tags_Placeholder'
                  )}
                />

                <span>
                  {i18n.translate('AdminHTML.ProductVisibility.ProductVisibility.appliesTo')}
                </span>
                <Stack distribution="fill">
                  <Field
                    name="appliesTo.collectionPage"
                    component={RenderCheckbox}
                    label={i18n.translate(
                      'AdminHTML.ProductVisibility.ProductVisibility.appliesTo_CollectionPage'
                    )}
                  />
                  <Field
                    name="appliesTo.searchPage"
                    component={RenderCheckbox}
                    label={i18n.translate(
                      'AdminHTML.ProductVisibility.ProductVisibility.appliesTo_SearchPage'
                    )}
                  />
                  <Field
                    name="appliesTo.instantSearchWidget"
                    component={RenderCheckbox}
                    label={i18n.translate(
                      'AdminHTML.ProductVisibility.ProductVisibility.appliesTo_InstantSearchWidget'
                    )}
                  />
                </Stack>
              </FormLayout>
            </form>
          </div>
        </Modal.Section>
      </Modal>
    );
  }

  handleChange = () => {
    this.props.resetForm();
    this.props.handleChange();
  };

  handleSave = async data => {
    const { productVisibilityRules, updateProductVisibilityRules, selectedId } = this.props;
    let saveData = [...productVisibilityRules];

    const updatedData = buildProductVisibilityRule(data);
    if (selectedId !== -1) {
      // Update product visibility rule
      saveData[selectedId] = updatedData;
    } else {
      // Create new product visibility rule
      saveData.push(updatedData);
    }
    await updateProductVisibilityRules(saveData);
    this.handleChange();
  };

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }
}

const ProductVisibilityForm = reduxForm({
  form: 'ProductVisibilityForm', // a unique identifier for this form
  enableReinitialize: true
})(ProductVisibilityModal);

const mapStateToProps = (state, props) => {
  const productVisibilityRules = _.get(state.productVisibility, 'productVisibilityRules', []);
  const { selectedId } = props;
  const productVisibilityRule = selectedId === -1 ? {} : productVisibilityRules[selectedId];

  const formValues = getFormValues('ProductVisibilityForm')(state);

  const saving = _.get(state.productVisibility, 'saving', false);
  const success = _.get(state.productVisibility, 'success', false);
  return {
    productVisibilityRules,
    tags: _.get(formValues, 'tags', ''),
    collectionPage: _.get(formValues, 'appliesTo.collectionPage', false),
    searchPage: _.get(formValues, 'appliesTo.searchPage', false),
    instantSearchWidget: _.get(formValues, 'appliesTo.instantSearchWidget', false),
    initialValues: buildInitialValues(productVisibilityRule),
    saving,
    success
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateProductVisibilityRules: productVisibilityRules =>
    dispatch(
      productVisibilityActions.updateProductVisibilityRules(productVisibilityRules, ownProps.i18n)
    ),
  // Reset form
  resetForm: () => dispatch(reset('ProductVisibilityForm'))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ProductVisibilityForm));
